// frontend/src/pages/QuotesPage.jsx
// Project:      Vision ANPR by Castle Traffic Ltd - Automatic Number Plate Recognition
// Author:       Leon du Plooy - Castle Traffic Ltd - Copyright 2026
// Contact:      dev@castle-traffic.co.uk
// Created:      2026-01-27
// Updated:      2026-10-27 11:40 GMT
//
// Change Summary (2026-10-27 11:40 GMT):
//   - Registered Reusable Quote Notes as an optional Quotes-surface widget.
//   - Separated the picker-supported catalogue from first-use default widgets.
//   - Preserved user-selected widget and desktop/mobile layout preferences.
//   - Uses canonical registry metadata for new widgets with local legacy fallbacks.
//
// Desktop resize regression fix (2026-09-25):
//   - Removed the viewport-derived ReactGridLayout `maxRows` ceiling.
//   - Desktop widgets may now grow vertically beyond the initial viewport and the page scrolls naturally.
//   - Layout persistence preserves x/y/w/h while preventing negative y positions.
//   - Page-owned widget positioning is no longer forced back inside viewport rows.
//   - Existing mobile single-column presentation and mobile sizing behaviour are unchanged.
//
// Description:
//   Quotes workspace page (dashboard-style GridLayout; matches OpportunitiesPage pattern).
//   Default widgets:
//     - quote_stage_progress (top row)
//     - quote_inbox (left)
//     - quote_detail (middle)
//     - quote_status_summary (right-top)
//     - quote_line_items (right-mid)
//     - terms_and_conditions (right-bottom)
//     - pricebooks_admin (right-bottom-2)
//
// Notes:
//   - Uses shared Axios instance (api) ONLY.
//   - Persists layout + widgets in localStorage.
//   - Persists last selected quote across refresh in localStorage.
//   - Create/Edit modal component: frontend/src/components/QuoteCreateModal.jsx
//
// Fixes (2026-02-05):
//   - Restores layout persistence without ever “dropping” default widgets.
//   - Guarantees Terms & Conditions widget cannot disappear due to stale localStorage.
//   - Adds safe add-widget wiring hooks (supports multiple callback prop names).
//   - Ensures the grid renders from active widget ids + layout, not an overwritten default.
//
// Fixes (2026-02-10):
//   - NEW QUOTE button reliability: stop react-grid-layout drag/capture swallowing clicks (mousedown/pointerdown/touchstart).
//   - Wire onCreate into QuoteCreateModal so Create mode works (POST /api/quotes).
//
// Fixes (2026-09-02):
//   - Widget add/remove controls available to Super Admin, System Admin and System Manager.
//   - Removed widgets remain removed; defaults are used only on first use.
//   - Layout, widget selection and selected quote persist per tenant + per user.
//   - Quote widget catalogue is local to this page, avoiding an unnecessary /api/widgets request.
//
// Mobile Responsiveness (2026-09-04):
//   - Added a dedicated <=768px presentation.
//   - Mobile renders active Quote widgets as a natural single-column stream.
//   - Mobile ordering follows the persisted desktop layout position (y, then x).
//   - Mobile never persists layout changes, so mobile viewing cannot alter the user's
//     desktop widget positions or sizes.
//   - Reflowed Quotes heading, Add Widget and New Quote controls for compact widths.
//   - Preserved tenant/user-scoped widget preferences, quote selection persistence,
//     New/Edit Quote modal behaviour and all existing widget/API wiring.

import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import GridLayout from "react-grid-layout";
import "react-grid-layout/css/styles.css";
import "react-resizable/css/styles.css";

import api from "../api/axiosInstance";
import { useTheme } from "../context/ThemeContext";
import { useAuth } from "../context/AuthContext";

import WidgetWrapper from "../components/WidgetWrapper";
import AddWidgetModal from "../components/AddWidgetModal";
import QuoteCreateModal from "../components/QuoteCreateModal";

import QuoteInboxWidget from "../widgets/QuoteInboxWidget";
import QuoteDetailWidget from "../widgets/QuoteDetailWidget";
import QuoteLineItemsWidget from "../widgets/QuoteLineItemsWidget";
import QuoteStageProgressWidget from "../widgets/QuoteStageProgressWidget";
import QuoteStatusSummaryWidget from "../widgets/QuoteStatusSummaryWidget";
import TermsAndConditionsWidget from "../widgets/TermsAndConditionsWidget.jsx";
import PricebooksAdminWidget from "../widgets/PricebooksAdminWidget.jsx";
import QuoteNoteSnippetsWidget from "../widgets/QuoteNoteSnippetsWidget.jsx";
import { getWidgetById } from "../widgets/WidgetRegistry";

// ✅ Weather widget (match the exact filename you already used on Jobs page)
import WeatherSpotWidget from "../widgets/WeatherSpotWidget.jsx";

// -------------------- Widget preference identity --------------------
const MANAGE_WIDGET_ROLES = new Set(["Super Admin", "System Admin", "System Manager"]);
const MANAGE_WIDGET_ROLE_IDS = new Set([1, 3, 4]);
const MOBILE_BREAKPOINT = 768;

function useIsMobile(breakpoint = MOBILE_BREAKPOINT) {
  const [isMobile, setIsMobile] = useState(() => {
    if (typeof window === "undefined" || !window.matchMedia) return false;
    return window.matchMedia(`(max-width: ${breakpoint}px)`).matches;
  });

  useEffect(() => {
    if (typeof window === "undefined" || !window.matchMedia) return undefined;

    const mediaQuery = window.matchMedia(`(max-width: ${breakpoint}px)`);
    const apply = () => setIsMobile(mediaQuery.matches);

    apply();

    if (mediaQuery.addEventListener) {
      mediaQuery.addEventListener("change", apply);
    } else {
      mediaQuery.addListener(apply);
    }

    return () => {
      if (mediaQuery.removeEventListener) {
        mediaQuery.removeEventListener("change", apply);
      } else {
        mediaQuery.removeListener(apply);
      }
    };
  }, [breakpoint]);

  return isMobile;
}

const getRoleName = (user) => {
  const direct = user?.role_name ?? user?.roleName;
  if (direct) return String(direct);
  if (typeof user?.role === "string") return user.role;
  return String(user?.role?.name ?? user?.role?.role_name ?? user?.role?.title ?? user?.role?.label ?? "");
};

const canManageWidgetsForUser = (user) => {
  const roleName = getRoleName(user);
  const roleId = Number(user?.role_id ?? user?.role?.id ?? 0);
  return MANAGE_WIDGET_ROLES.has(roleName) || MANAGE_WIDGET_ROLE_IDS.has(roleId);
};

const buildStorageKeys = (user) => {
  const tenantId = user?.tenant_id ?? "anonTenant";
  const userId = user?.id ?? user?.user_id ?? user?.username ?? "anonUser";
  return {
    layout: `quotesLayout_${tenantId}_${userId}`,
    widgets: `quotesWidgets_${tenantId}_${userId}`,
    selected: `quotesSelectedQuoteId_${tenantId}_${userId}`,
  };
};

// -------------------- Grid interaction hardening --------------------
// react-grid-layout captures mousedown/pointerdown and can swallow clicks.
// For any clickable control inside grid widgets, stop propagation on down.
const stopGridDrag = (e) => {
  try {
    e.preventDefault?.();
  } catch {}
  e.stopPropagation();
};

// -------------------- Helpers --------------------
const normaliseLayout = (layout) =>
  (Array.isArray(layout) ? layout : []).map((item) => {
    const currentY = Number.isFinite(item?.y) ? item.y : 0;
    return { ...item, y: Math.max(0, currentY) };
  });

const safeInt = (v) => {
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
};

const safeJsonParse = (raw) => {
  try {
    return JSON.parse(raw);
  } catch {
    return null;
  }
};

const uniq = (arr) => {
  const out = [];
  const seen = new Set();
  (arr || []).forEach((x) => {
    const k = String(x);
    if (!seen.has(k)) {
      seen.add(k);
      out.push(k);
    }
  });
  return out;
};

const titleForWidgetId = (id) => {
  switch (id) {
    case "quote_stage_progress":
      return "Quote Stage Progress";
    case "quote_inbox":
      return "Quote Inbox";
    case "quote_detail":
      return "Quote Details";
    case "quote_status_summary":
      return "Status Summary";
    case "quote_line_items":
      return "Line Items";
    case "terms_and_conditions":
      return "Terms & Conditions";
    case "pricebooks_admin":
      return "Pricebooks (Admin)";
    case "weather_spot":
      return "Weather Forecast";
    case "quote_note_snippets":
      return "Reusable Quote Notes";
    default:
      return id;
  }
};

// -------------------- Default layout --------------------
const defaultLayout = [
  { i: "quote_stage_progress", x: 0, y: 0, w: 12, h: 3, minW: 6, minH: 3, maxW: 12 },
  { i: "quote_inbox", x: 0, y: 3, w: 4, h: 11, minW: 3, minH: 6, maxW: 6 },
  { i: "quote_detail", x: 4, y: 3, w: 5, h: 11, minW: 4, minH: 8, maxW: 8 },

  // Right column: stack
  { i: "quote_status_summary", x: 9, y: 3, w: 3, h: 3, minW: 3, minH: 3, maxW: 6 },
  { i: "quote_line_items", x: 9, y: 6, w: 3, h: 4, minW: 3, minH: 4, maxW: 6 },
  { i: "terms_and_conditions", x: 9, y: 10, w: 3, h: 3, minW: 3, minH: 3, maxW: 6 },
  { i: "pricebooks_admin", x: 9, y: 13, w: 3, h: 5, minW: 3, minH: 4, maxW: 6 },

  // ✅ NEW: Weather widget (defaults on the page; user can move/resize)
  { i: "weather_spot", x: 0, y: 14, w: 4, h: 6, minW: 3, minH: 4, maxW: 12 },
];

const DEFAULT_WIDGET_IDS = defaultLayout.map((x) => x.i);
// Picker support is separate from first-use defaults: do not automatically add new widgets.
const ADDITIONAL_WIDGET_IDS = ["quote_note_snippets"];
const SUPPORTED_WIDGET_IDS = [...DEFAULT_WIDGET_IDS, ...ADDITIONAL_WIDGET_IDS];
const SUPPORTED_WIDGET_SET = new Set(SUPPORTED_WIDGET_IDS);

const validateLayout = (layout, allowedIds) => {
  const allowed = new Set((allowedIds || []).map(String));
  const l = Array.isArray(layout) ? layout : [];
  const normalized = l
    .filter((x) => x && typeof x.i === "string" && allowed.has(x.i))
    .map((x) => {
      const base = defaultLayout.find((d) => d.i === x.i) || {};
      return {
        ...base, // keeps minW/minH/maxW/maxH defaults stable
        ...x,
        i: String(x.i),
        x: Number.isFinite(Number(x.x)) ? Number(x.x) : base.x ?? 0,
        y: Number.isFinite(Number(x.y)) ? Number(x.y) : base.y ?? 0,
        w: Number.isFinite(Number(x.w)) ? Number(x.w) : base.w ?? 3,
        h: Number.isFinite(Number(x.h)) ? Number(x.h) : base.h ?? 3,
      };
    });

  // Ensure every allowed widget has a layout item (fallback to default positions).
  const byId = new Map(normalized.map((x) => [x.i, x]));
  const merged = [];
  for (const id of allowed) {
    merged.push(byId.get(id) || { ...(defaultLayout.find((d) => d.i === id) || { i: id, x: 0, y: 0, w: 3, h: 3 }) });
  }

  return normaliseLayout(merged);
};

export default function QuotesPage() {
  const { theme } = useTheme();
  const { user } = useAuth();
  const isMobile = useIsMobile();
  const canManageWidgets = canManageWidgetsForUser(user);
  const storageKeys = useMemo(() => buildStorageKeys(user), [user?.tenant_id, user?.id, user?.user_id, user?.username]);

  const [showAddModal, setShowAddModal] = useState(false);
  const [widgetCatalog, setWidgetCatalog] = useState([]);

  const [layout, setLayout] = useState([]);
  const [activeWidgetIds, setActiveWidgetIds] = useState([]);

  const [gridWidth, setGridWidth] = useState(1200);
  const gridRef = useRef(null);

  const [showCreate, setShowCreate] = useState(false);
  const [editingQuoteId, setEditingQuoteId] = useState(null);

  const [selectedQuoteId, setSelectedQuoteId] = useState(null);

  const [refreshKey, setRefreshKey] = useState(0);
  const [customerMap, setCustomerMap] = useState({});
  const [customersErr, setCustomersErr] = useState("");

  const containerPadding = 24;
  const headerHeight = 64;
  const rowHeight = 40;

  const scrollSafe = { height: "100%", minHeight: 0, overflow: "auto", paddingRight: 2 };

  // --------- init persisted state ------------------------------------------
  // Defaults are first-use defaults only. Once a user has saved a widget list,
  // that list is authoritative, including an intentionally empty list.
  useEffect(() => {
    if (!user) return;

    let ids = [...DEFAULT_WIDGET_IDS];
    let hasSavedWidgets = false;

    try {
      const raw = localStorage.getItem(storageKeys.widgets);
      if (raw !== null) {
        const parsed = safeJsonParse(raw);
        if (Array.isArray(parsed)) {
          ids = uniq(parsed.map(String).filter((id) => SUPPORTED_WIDGET_SET.has(id)));
          hasSavedWidgets = true;
        }
      }
    } catch {}

    let nextLayout = [];
    try {
      const rawL = localStorage.getItem(storageKeys.layout);
      const parsedL = safeJsonParse(rawL);
      nextLayout = validateLayout(Array.isArray(parsedL) ? parsedL : [], ids);
    } catch {
      nextLayout = validateLayout([], ids);
    }

    let selected = null;
    try {
      const rawSelected = localStorage.getItem(storageKeys.selected);
      const n = safeInt(rawSelected);
      selected = n && n > 0 ? n : null;
    } catch {}

    setActiveWidgetIds(ids);
    setLayout(nextLayout);
    setSelectedQuoteId(selected);

    // Establish scoped first-use state without resurrecting removed widgets later.
    if (!hasSavedWidgets) {
      try {
        localStorage.setItem(storageKeys.widgets, JSON.stringify(ids));
        localStorage.setItem(storageKeys.layout, JSON.stringify(nextLayout));
      } catch {}
    }
  }, [user, storageKeys]);

  // Keep the scoped layout validated without imposing a vertical page ceiling.
  useEffect(() => {
    if (!activeWidgetIds.length) return;
    const next = validateLayout(layout, activeWidgetIds);
    setLayout(next);
    try {
      localStorage.setItem(storageKeys.layout, JSON.stringify(next));
    } catch {}
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [storageKeys.layout]);

  // --------- selection + modal helpers ------------------------------------
  const selectQuote = useCallback((id) => {
    const n = safeInt(id);
    setSelectedQuoteId(n);
    try {
      if (n) localStorage.setItem(storageKeys.selected, String(n));
      else localStorage.removeItem(storageKeys.selected);
    } catch {}
  }, [storageKeys.selected]);

  const openCreateModal = useCallback(() => {
    setEditingQuoteId(null);
    setShowCreate(true);
  }, []);

  const openEditModal = useCallback(
    (id) => {
      const qid = safeInt(id);
      if (!qid) return;
      selectQuote(qid);
      setEditingQuoteId(qid);
      setShowCreate(true);
    },
    [selectQuote]
  );

  const closeModal = useCallback(() => {
    setShowCreate(false);
    setEditingQuoteId(null);
  }, []);

  // --------- quote change propagation --------------------------------------
  // Existing quote mutations (for example status changes made inside the
  // create/edit modal or stage progress widget) must invalidate every quote
  // widget on this page. refreshKey is already the page-wide invalidation
  // mechanism consumed by those widgets.
  const handleQuoteChanged = useCallback(() => {
    setRefreshKey((k) => k + 1);
  }, []);

  // --------- onCreate handler (modal dependency) ---------------------------
  const handleCreateQuote = useCallback(
    async (payload) => {
      // Server is source of truth. This must be the same API all widgets use.
      const resp = await api.post("/api/quotes", payload);
      const created = resp?.data || null;

      // After create, refresh widgets/inbox and auto-select the new quote.
      const newId = safeInt(created?.id);
      if (newId && newId > 0) {
        selectQuote(newId);
        setRefreshKey((k) => k + 1);
      } else {
        // still refresh to reflect any backend changes
        setRefreshKey((k) => k + 1);
      }
      return created;
    },
    [selectQuote]
  );

  // --------- grid width ----------------------------------------------------
  // Use ResizeObserver on the actual grid container so ReactGridLayout widens/narrows
  // when available horizontal space changes, including sidebar collapse/expand.
  useEffect(() => {
    const node = gridRef.current;
    if (!node) return undefined;

    const updateWidth = () => {
      const nextWidth = Math.floor(node.getBoundingClientRect().width || node.offsetWidth || 1200);
      setGridWidth(nextWidth);
    };

    updateWidth();

    if (typeof ResizeObserver === "undefined") {
      window.addEventListener("resize", updateWidth);
      return () => window.removeEventListener("resize", updateWidth);
    }

    const observer = new ResizeObserver(updateWidth);
    observer.observe(node);

    return () => observer.disconnect();
  }, []);

  // --------- data loads ----------------------------------------------------
  useEffect(() => {
    api
      .get("/api/customers", { params: { limit: 1000, offset: 0 } })
      .then((res) => {
        const map = {};
        (Array.isArray(res.data) ? res.data : []).forEach((c) => {
          if (c?.id) map[String(c.id)] = c.name || c.display_name || `Customer ${c.id}`;
        });
        setCustomerMap(map);
      })
      .catch((e) => setCustomersErr(e?.message || "Failed to load customers"));
  }, []);

  useEffect(() => {
    setWidgetCatalog(
      SUPPORTED_WIDGET_IDS.map((id) => {
        const canonical = getWidgetById(id);
        return {
          id,
          widget_id: id,
          friendly_name: canonical?.friendly_name || titleForWidgetId(id),
          description: canonical?.description || "",
          category: canonical?.category || "Sales",
        };
      })
    );
  }, []);

  // --------- widget resolver ------------------------------------------------
  const resolveWidgetComponent = useCallback(
    (id) => {
      switch (id) {
        case "quote_stage_progress":
          return (
            <QuoteStageProgressWidget
              quoteId={selectedQuoteId}
              refreshKey={refreshKey}
              onQuoteChanged={handleQuoteChanged}
            />
          );

        case "quote_inbox":
          return (
            <>
              <div className="flex justify-between mb-2">
                <div className="text-xs opacity-75">Select a quote, or create a new one.</div>
                <button
                  className="bg-btnActionNormal hover:bg-btnActionHover active:bg-btnActionClick text-white px-3 py-2 rounded"
                  onMouseDown={stopGridDrag}
                  onPointerDown={stopGridDrag}
                  onTouchStart={stopGridDrag}
                  onClick={(e) => {
                    e.stopPropagation();
                    openCreateModal();
                  }}
                  type="button"
                >
                  New Quote
                </button>
              </div>
              <QuoteInboxWidget
                selectedQuoteId={selectedQuoteId}
                onSelectQuote={selectQuote}
                onEditQuote={(payload) => {
                  const raw = payload?.quoteId ?? payload?.id ?? payload?.quote_id ?? payload;
                  const qid = safeInt(raw);
                  if (qid) openEditModal(qid);
                }}
                refreshKey={refreshKey}
                customerMap={customerMap}
              />
            </>
          );

        case "quote_detail":
          return <QuoteDetailWidget quoteId={selectedQuoteId} refreshKey={refreshKey} customerMap={customerMap} />;

        case "quote_status_summary":
          return <QuoteStatusSummaryWidget selectedQuoteId={selectedQuoteId} refreshKey={refreshKey} />;

        case "quote_line_items":
          return <QuoteLineItemsWidget quoteId={selectedQuoteId} refreshKey={refreshKey} />;

        case "terms_and_conditions":
          return (
            <div style={scrollSafe}>
              <TermsAndConditionsWidget />
            </div>
          );

        case "quote_note_snippets":
          return (
            <div style={scrollSafe}>
              <QuoteNoteSnippetsWidget />
            </div>
          );

        case "pricebooks_admin":
          return (
            <div style={scrollSafe}>
              <PricebooksAdminWidget />
            </div>
          );

        case "weather_spot":
          return (
            <div style={scrollSafe}>
              <WeatherSpotWidget />
            </div>
          );

        default:
          return <div className="text-red-600 p-2">Unknown widget: {String(id)}</div>;
      }
    },
    [customerMap, handleQuoteChanged, openCreateModal, openEditModal, refreshKey, scrollSafe, selectedQuoteId, selectQuote]
  );

  // --------- persistence handlers ------------------------------------------
  const persistWidgets = useCallback((ids) => {
    try {
      localStorage.setItem(storageKeys.widgets, JSON.stringify(ids));
    } catch {}
  }, [storageKeys.widgets]);

  const persistLayout = useCallback((l) => {
    try {
      localStorage.setItem(storageKeys.layout, JSON.stringify(l));
    } catch {}
  }, [storageKeys.layout]);

  const onLayoutChange = useCallback(
    (l) => {
      const next = validateLayout(l, activeWidgetIds);
      setLayout(next);
      persistLayout(next);
    },
    [activeWidgetIds, persistLayout]
  );

  // --------- add widget wiring (supports multiple modal APIs) ---------------
  const addWidgetById = useCallback(
    (raw) => {
      if (!canManageWidgets) return;
      const id = String(raw?.id ?? raw?.widget_id ?? raw?.name ?? raw);
      if (!SUPPORTED_WIDGET_SET.has(id)) return; // this page only supports its known widgets
      const nextIds = uniq([...activeWidgetIds, id]);
      setActiveWidgetIds(nextIds);
      persistWidgets(nextIds);

      const nextLayout = validateLayout(layout, nextIds);
      setLayout(nextLayout);
      persistLayout(nextLayout);

      setShowAddModal(false);
    },
    [activeWidgetIds, canManageWidgets, layout, persistLayout, persistWidgets]
  );

  const closeAddModal = useCallback(() => setShowAddModal(false), []);

  const removeWidgetById = useCallback(
    (widgetId) => {
      if (!canManageWidgets) return;
      const id = String(widgetId);
      const nextIds = activeWidgetIds.filter((x) => x !== id);
      const nextLayout = layout.filter((item) => item.i !== id);

      setActiveWidgetIds(nextIds);
      setLayout(nextLayout);
      persistWidgets(nextIds);
      persistLayout(nextLayout);
    },
    [activeWidgetIds, canManageWidgets, layout, persistLayout, persistWidgets]
  );

  /*
   * Mobile is only a presentation of the user's existing Quote workspace.
   * It follows desktop visual order without writing mobile positions back
   * into the tenant/user-scoped layout preference.
   */
  const mobileLayoutItems = useMemo(() => {
    const activeSet = new Set(activeWidgetIds);
    return layout
      .filter((item) => activeSet.has(item.i))
      .slice()
      .sort((a, b) => {
        const ay = Number.isFinite(Number(a?.y)) ? Number(a.y) : Number.MAX_SAFE_INTEGER;
        const by = Number.isFinite(Number(b?.y)) ? Number(b.y) : Number.MAX_SAFE_INTEGER;
        if (ay !== by) return ay - by;

        const ax = Number.isFinite(Number(a?.x)) ? Number(a.x) : Number.MAX_SAFE_INTEGER;
        const bx = Number.isFinite(Number(b?.x)) ? Number(b.x) : Number.MAX_SAFE_INTEGER;
        if (ax !== bx) return ax - bx;

        return String(a?.i || "").localeCompare(String(b?.i || ""));
      });
  }, [activeWidgetIds, layout]);

  const getMobileWidgetHeight = useCallback(
    (item) => {
      const h = Number.isFinite(Number(item?.h)) ? Math.max(1, Number(item.h)) : 4;
      return Math.max(280, h * rowHeight + Math.max(0, h - 1) * 24);
    },
    [rowHeight]
  );

  return (
    <div
      style={{
        backgroundColor: "#f0f0f0",
        minHeight: isMobile ? "100%" : "100vh",
        overflowX: "hidden",
        paddingLeft: isMobile ? 12 : containerPadding,
        paddingRight: isMobile ? 12 : containerPadding,
        paddingTop: isMobile ? 4 : 16,
        paddingBottom: isMobile ? 16 : 24,
        boxSizing: "border-box",
        fontFamily: theme?.font_body || "inherit",
      }}
    >
      <div ref={gridRef} className="w-full" style={{ minWidth: 0 }}>
        <div
          className={isMobile ? undefined : "flex items-center justify-between mb-4"}
          style={{
            minHeight: isMobile ? 58 : headerHeight,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: isMobile ? 10 : undefined,
            marginBottom: isMobile ? 6 : undefined,
          }}
        >
          <div
            className={isMobile ? undefined : "text-xl font-semibold"}
            style={{
              color: theme?.accent_color || "#ff7a00",
              fontSize: isMobile ? "1.35rem" : undefined,
              fontWeight: 600,
              lineHeight: 1.2,
              minWidth: 0,
            }}
          >
            Quotes
          </div>

          <div
            className={isMobile ? undefined : "flex items-center gap-2"}
            style={{
              display: "flex",
              alignItems: "center",
              gap: isMobile ? 8 : undefined,
              flexWrap: isMobile ? "wrap" : "nowrap",
              justifyContent: "flex-end",
            }}
          >
            {canManageWidgets && (
            <button
              type="button"
              className="bg-btnGeneralNormal hover:bg-btnGeneralHover active:bg-btnGeneralClick text-white px-3 py-2 rounded"
              style={{ minHeight: isMobile ? 44 : undefined, whiteSpace: "nowrap" }}
              onMouseDown={stopGridDrag}
              onPointerDown={stopGridDrag}
              onTouchStart={stopGridDrag}
              onClick={() => setShowAddModal(true)}
            >
              Add Widget
            </button>
            )}

            <button
              type="button"
              className="bg-btnActionNormal hover:bg-btnActionHover active:bg-btnActionClick text-white px-3 py-2 rounded"
              style={{ minHeight: isMobile ? 44 : undefined, whiteSpace: "nowrap" }}
              onMouseDown={stopGridDrag}
              onPointerDown={stopGridDrag}
              onTouchStart={stopGridDrag}
              onClick={(e) => {
                e.stopPropagation();
                openCreateModal();
              }}
            >
              New Quote
            </button>
          </div>
        </div>

        {customersErr && <div className="text-red-600 font-bold mb-2">{customersErr}</div>}

        {isMobile ? (
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              gap: 12,
              width: "100%",
              minWidth: 0,
              paddingBottom: 8,
            }}
          >
            {mobileLayoutItems.map((it) => (
              <div
                key={it.i}
                style={{
                  width: "100%",
                  minWidth: 0,
                  height: `${getMobileWidgetHeight(it)}px`,
                  overflow: "hidden",
                }}
              >
                <WidgetWrapper
                  title={titleForWidgetId(it.i)}
                  onRemove={canManageWidgets ? () => removeWidgetById(it.i) : undefined}
                  showRemoveButton={canManageWidgets}
                >
                  {resolveWidgetComponent(it.i)}
                </WidgetWrapper>
              </div>
            ))}
          </div>
        ) : (
          <GridLayout
            layout={layout}
            cols={12}
            rowHeight={rowHeight}
            width={gridWidth}
            margin={[16, 24]}
            onLayoutChange={onLayoutChange}
          >
            {layout
              .filter((it) => activeWidgetIds.includes(it.i))
              .map((it) => (
                <div key={it.i}>
                  <WidgetWrapper
                    title={titleForWidgetId(it.i)}
                    onRemove={canManageWidgets ? () => removeWidgetById(it.i) : undefined}
                    showRemoveButton={canManageWidgets}
                  >
                    {resolveWidgetComponent(it.i)}
                  </WidgetWrapper>
                </div>
              ))}
          </GridLayout>
        )}

        {showAddModal && canManageWidgets && (
          <AddWidgetModal
            widgets={widgetCatalog}
            onClose={closeAddModal}
            // Support multiple possible callback prop names without knowing the modal implementation.
            onAdd={addWidgetById}
            onAddWidget={addWidgetById}
            onSelect={addWidgetById}
          />
        )}

        <QuoteCreateModal
          key={`${showCreate ? "open" : "closed"}-${editingQuoteId || "new"}`}
          open={showCreate}
          onClose={closeModal}
          customerMap={customerMap}
          contextQuoteId={editingQuoteId}
          onCreate={handleCreateQuote}
          onQuoteChanged={handleQuoteChanged}
        />
      </div>
    </div>
  );
}